import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastService, ToastType } from '@pin-up-global/common';
import { Pagination } from '@root/shared/abstraction';
import { DEFAULT_PAGINATION_PARAMS } from '@root/shared/table';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  map,
  Observable,
  ReplaySubject,
  shareReplay,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { FlowsLocalizationApiService } from '.';
import { FlowContentItemTable, FlowContentItemTranslation, FlowsLocalsReq } from './flows-localization.abstraction';
import { BotsService } from '@core/bots';

@Injectable()
export class FlowsLocalizationService {
  #flowsLocalizationApiService = inject(FlowsLocalizationApiService);
  #translateService = inject(TranslateService);
  #toastService = inject(ToastService);
  #botsService = inject(BotsService);
  #router = inject(Router);

  #flowContentId$ = new ReplaySubject<number>();
  #filters = new ReplaySubject<Partial<FlowsLocalsReq>>();
  #filtersByReq = new BehaviorSubject<boolean>(false);
  #update$ = new BehaviorSubject<void>(void 0);

  #flowsLocalsRes$ = this.#update$.pipe(
    switchMap(() => combineLatest([this.#filters, this.#flowContentId$])),
    debounceTime(500),
    switchMap(([filters, flowContentId]) =>
      this.#flowsLocalizationApiService.getFlowContent({
        ...filters,
        flowContentId,
      })
    ),
    shareReplay(1)
  );

  #flowsTranslateStatisticsRes$ = this.#update$.pipe(
    switchMap(() => this.#flowContentId$),
    debounceTime(500),
    switchMap(flowContentId => this.#flowsLocalizationApiService.getTranslationsStatistics({flowContentId})
    ),
    shareReplay(1)
  );

  readonly flowsLocals$ = this.#flowsLocalsRes$.pipe(
    withLatestFrom(
      this.#botsService.supportedLocales$.pipe(
        map((locales) => locales.find((el) => el.isDefault))
      )
    ),
    map(([flowsLocals, locale]) => {
      return flowsLocals.items.map((el) => {
        let newObj: FlowContentItemTable = {
          ...el,
          translationsObj: {},
        };

        el.translations.map((element) => {
          newObj.translationsObj[element.localeId] = element;
        });

        newObj.translationsObj[locale!.id] = {
          createdAt: el.createdAt,
          id: el.id,
          localeId: locale!.id,
          reviewRequired: false,
          updatedAt: el.updatedAt,
          value: el.value,
        };

        return newObj;
      });
    }),
    switchMap((flowsLocals) =>
      this.#filtersByReq.pipe(
        map((filter) =>
          filter
            ? flowsLocals.filter((item) => Object.values(item.translationsObj).some((el) => !el.value || el.reviewRequired))
            : flowsLocals
        )
      )
    )
  );

  readonly flowsTranslationsStatistics$ = this.#flowsTranslateStatisticsRes$.pipe(map((res) => res));

  readonly pagination$: Observable<Pagination> = this.#flowsLocalsRes$.pipe(
    map((res) => res.count),
    withLatestFrom(this.#filters),
    map(([count, filters]) => ({
      count,
      page: filters.page!,
      limit: filters.limit!,
    })),
    startWith(DEFAULT_PAGINATION_PARAMS)
  );

  readonly isLoading$ = this.#flowsLocalizationApiService.isLoading$;

  setFlowContentId(flowContentId: number): void {
    this.#flowContentId$.next(flowContentId);
  }

  updateFilters(filters: Partial<FlowsLocalsReq>): void {
    this.#filters.next(filters);
  }

  refresh(): void {
    this.#update$.next();
  }

  resetFilters(): void {
    this.#router.navigate([], {
      relativeTo: this.#router.routerState.root,
      queryParams: {},
    });
  }

  updateForm(id: number, value: string): Observable<FlowContentItemTranslation> {
    return this.#flowsLocalizationApiService
      .patchTranslation({ id, value })
      .pipe(
        tap(() => {
          this.#toastService.show({
            type: ToastType.Success,
            title: this.#translateService.instant('flowsLocalizationPage.toast.updated'),
          });
          this.refresh();
        })
      );
  }

  filterItems(hasFilter: boolean): void {
    this.#filtersByReq.next(hasFilter);
  }
}
